import styled from '@emotion/styled';
import { Radio, RadioProps } from '@mui/material';

import { COLORS } from '@/styles/tokens/colors';

const StyledIcon = styled('svg', {
  shouldForwardProp: (prop) => prop !== 'checked',
})<Pick<RadioProps, 'checked'>>(({ checked }) => ({
  borderRadius: '50%',
  color: checked ? COLORS.GRAY[700] : COLORS.GRAY[500],

  'input:hover:not(:disabled) ~ &': {
    color: COLORS.GRAY[800],
  },
  'input:disabled ~ &': {
    color: COLORS.GRAY[300],
  },
  '.Mui-focusVisible &': {
    outline: `2px solid ${COLORS.GRAY[800]}`,
    outlineOffset: 2,
  },
}));

function RadioIcon({ size, checked }: Pick<RadioProps, 'size' | 'checked'>) {
  const dimension = size === 'small' ? 16 : 20;
  const center = dimension / 2;

  return (
    <StyledIcon
      aria-hidden="true"
      checked={checked}
      width={dimension}
      height={dimension}
      viewBox={`0 0 ${dimension} ${dimension}`}
      fill={COLORS.PRIMITIVES.WHITE}
    >
      <circle cx={center} cy={center} r={center - 0.5} stroke="currentColor" />
      {checked && (
        // Paint both circles around the same center, including fractional dot sizes.
        <circle
          cx={center}
          cy={center}
          r={dimension * 0.3125}
          fill="currentColor"
        />
      )}
    </StyledIcon>
  );
}

export function StyledRadioButton({ size, ...props }: RadioProps) {
  return (
    <Radio
      sx={{
        '&:hover': {
          bgcolor: 'transparent',
        },
        py: 0.5,
      }}
      disableRipple
      color="default"
      checkedIcon={<RadioIcon size={size} checked />}
      icon={<RadioIcon size={size} />}
      inputProps={
        {
          'data-testid': props.id,
        } as React.InputHTMLAttributes<HTMLInputElement>
      }
      {...props}
    />
  );
}
